/**
 * PercentageInput Component
 *
 * A specialized input component for percentage values with:
 * - Dot (.) as decimal separator (not comma)
 * - % suffix displayed in the input field
 * - Custom up/down arrow buttons for increment/decrement
 * - Configurable step increment (default 0.25)
 * - Always formats to 2 decimal places (e.g., "3.00%")
 * - Integrates with Formik for form state management
 *
 * Usage:
 * <Form.PercentageInput
 *   name="trigger_rate"
 *   label="Dream rate"
 *   step={0.25}
 *   tooltipText="The rate that triggers rate alert notification & emails to the lead."
 * />
 */

import { useState } from "react"
import { Field, ErrorMessage, FieldProps } from "formik"
import { ChevronUp, ChevronDown } from "lucide-react"
import Label from "./label"
import If from "../if"

type Props = {
  label: string
  name: string
  step?: number // default 0.25
  min?: number
  max?: number
  tooltipText?: React.ReactNode
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  helpText?: React.ReactNode
  labelAddon?: React.ReactNode
}

const CLASSNAME_DEFAULT = ""
const DEFAULT_SHOW_LABEL = true
const DEFAULT_STEP = 0.25

const PercentageInput = ({
  label,
  name,
  step = DEFAULT_STEP,
  min,
  max,
  tooltipText,
  placeholder,
  required = false,
  disabled = false,
  className = CLASSNAME_DEFAULT,
  helpText,
  labelAddon
}: Props) => {
  const [isFocused, setIsFocused] = useState(false)
  const [localString, setLocalString] = useState("")

  const formatValue = (value: number | string): string => {
    if (value === "" || value === null || value === undefined) return ""
    const numValue = typeof value === "string" ? parseFloat(value) : value
    if (isNaN(numValue)) return ""
    return numValue.toFixed(2) + "%"
  }

  const parseValue = (value: string | number): number => {
    if (typeof value === "number") return value
    if (typeof value !== "string") return 0
    const cleaned = value.replace(/[^\d.]/g, "")
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : parsed
  }

  const handleIncrement = (
    currentValue: number,
    setValue: (value: number) => void
  ) => {
    const newValue = currentValue + step
    if (max === undefined || newValue <= max) {
      setValue(newValue)
    }
  }

  const handleDecrement = (
    currentValue: number,
    setValue: (value: number) => void
  ) => {
    const newValue = currentValue - step
    if (min === undefined || newValue >= min) {
      setValue(newValue)
    }
  }

  const disabledClassName = disabled ? "text-neutral-400" : ""
  const fieldClassName = `appearance-none block w-full px-3 py-2.5 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 pr-10 ${disabledClassName}`

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Label
          className={disabledClassName}
          name={name}
          showLabel={DEFAULT_SHOW_LABEL}
          label={label}
          required={required}
          helperText={tooltipText}
        />
        {labelAddon}
      </div>
      <div className="mt-1 relative">
        <Field name={name}>
          {({ field, form }: FieldProps) => {
            const currentValue = parseValue(field.value || 0)
            const displayValue = isFocused
              ? localString
              : formatValue(field.value || 0)

            const setValue = (newValue: number) => {
              form.setFieldValue(name, newValue)
            }

            return (
              <>
                <input
                  {...field}
                  id={name}
                  type="text"
                  placeholder={placeholder}
                  className={fieldClassName}
                  disabled={disabled}
                  value={displayValue}
                  onFocus={() => {
                    setLocalString(formatValue(field.value || 0))
                    setIsFocused(true)
                  }}
                  onBlur={e => {
                    const parsed = parseValue(e.target.value)
                    if (!isNaN(parsed)) {
                      form.setFieldValue(name, parsed)
                    }
                    setLocalString("")
                    setIsFocused(false)
                  }}
                  onChange={e => {
                    const raw = e.target.value
                    const parsed = parseValue(raw)
                    setLocalString(raw)
                    form.setFieldValue(name, parsed)
                  }}
                />

                {/* Arrow buttons */}
                {!disabled && (
                  <div className="absolute inset-y-0 right-0 flex flex-col">
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-center px-2 border-l border-neutral-200 hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      onClick={() => {
                        handleIncrement(currentValue, setValue)
                        if (
                          isFocused &&
                          (max === undefined || currentValue + step <= max)
                        ) {
                          setLocalString(formatValue(currentValue + step))
                        }
                      }}
                      disabled={max !== undefined && currentValue >= max}
                    >
                      <ChevronUp className="h-3 w-3 text-neutral-500" />
                    </button>
                    <div className="border-t border-neutral-200"></div>
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-center px-2 border-l border-neutral-200 hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      onClick={() => {
                        handleDecrement(currentValue, setValue)
                        if (
                          isFocused &&
                          (min === undefined || currentValue - step >= min)
                        ) {
                          setLocalString(formatValue(currentValue - step))
                        }
                      }}
                      disabled={min !== undefined && currentValue <= min}
                    >
                      <ChevronDown className="h-3 w-3 text-neutral-500" />
                    </button>
                  </div>
                )}
              </>
            )
          }}
        </Field>
      </div>

      <If
        condition={!!helpText}
        then={<p className="mt-2 text-sm text-neutral-400">{helpText}</p>}
      />

      <ErrorMessage
        name={name}
        component="p"
        className="mt-2 text-sm text-red-600"
      />
    </div>
  )
}

export default PercentageInput
